
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Globe } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { QuizPreferences } from '@/types/quiz';
import { getDestinationScore } from '@/utils/destinationScoring';
import DestinationCard from '@/components/recommendations/DestinationCard';
import PaginationControls from '@/components/recommendations/PaginationControls';
import { renderHighlights } from '@/utils/destinationUtils';

const Recommendations = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const preferences = location.state?.recommendations as QuizPreferences;
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const { data: allDestinations, isLoading } = useQuery({
    queryKey: ['destinations', preferences],
    queryFn: async () => {
      console.log('Quiz preferences:', preferences);
      const { data, error } = await supabase
        .from('destinations')
        .select('*')
        .not('country', 'eq', 'Afghanistan')
        .not('country', 'eq', 'Ukraine');

      if (error) {
        console.error('Error fetching destinations:', error);
        throw error;
      }

      const scoredDestinations = data?.map(destination => ({
        ...destination,
        score: getDestinationScore(destination, preferences)
      }));

      return scoredDestinations?.sort((a, b) => b.score - a.score);
    },
    enabled: !!preferences,
  });

  // Calculate pagination
  const totalDestinations = allDestinations?.length || 0;
  const totalPages = Math.ceil(totalDestinations / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const destinations = allDestinations?.slice(startIndex, startIndex + itemsPerPage);

  if (!preferences) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container max-w-4xl mx-auto px-4 text-center py-12">
          <div className="text-gray-600 mb-8">
            Please complete the travel quiz to get personalized recommendations.
          </div>
          <Button onClick={() => navigate('/wizard')}>
            Take the Quiz
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container max-w-4xl mx-auto px-4 text-center py-12">
          <div className="text-gray-600">Finding Your Perfect Destinations...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="text-gray-600">
            Based on your preferences, we've found some destinations you might love
          </div>
          <div className="text-sm text-gray-500 mt-2">
            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, totalDestinations)} of {totalDestinations} destinations
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {destinations?.map((destination) => (
            <DestinationCard
              key={destination.id}
              destination={destination}
              preferences={preferences}
              renderHighlights={renderHighlights}
            />
          ))}
        </div>

        {totalPages > 1 && (
          <div className="mt-8">
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        <div className="mt-12 text-center">
          <Button
            variant="outline"
            onClick={() => navigate('/visa-requirements')}
            className="gap-2"
          >
            <Globe className="w-4 h-4" />
            Check Visa Requirements
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Recommendations;
